import React, { useState } from 'react';
import { LightbulbIcon, CheckIcon, XCircleIcon } from '@bizfirst/common-react';
import { ExecutionObserverConfig } from '../../models/ExecutionObserverConfig';
import '../../styles/tabs/AdvancedTab.css';

interface AdvancedTabProps {
  config: ExecutionObserverConfig;
  onChange: (config: ExecutionObserverConfig) => void;
  onValidate?: (isValid: boolean) => void;
  readonly?: boolean;
}

export const AdvancedTab: React.FC<AdvancedTabProps> = ({
  config,
  onChange,
  onValidate,
  readonly = false
}) => {
  const [jsonText, setJsonText] = useState(JSON.stringify(config, null, 2));
  const [error, setError] = useState<string | null>(null);

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      onChange(parsed);
      setError(null);
      onValidate?.(true);
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Invalid JSON';
      setError(errorMsg);
      onValidate?.(false);
    }
  };

  const handleValidate = () => {
    try {
      JSON.parse(jsonText);
      setError(null);
      onValidate?.(true);
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Invalid JSON';
      setError(errorMsg);
      onValidate?.(false);
    }
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (e) {
      setError('Cannot format invalid JSON');
    }
  };

  return (
    <fieldset className="advanced-tab" disabled={readonly}>
      <legend>Advanced: Raw JSON Editor</legend>

      <p className="description">
        Edit the observer configuration directly as JSON. Changes are applied in real-time.
      </p>

      <div className="json-editor-container">
        <textarea
          className={`json-editor ${error ? 'error' : ''}`}
          value={jsonText}
          onChange={(e) => handleJsonChange(e.target.value)}
          rows={20}
          spellCheck="false"
          disabled={readonly}
        />
      </div>

      {error && (
        <div className="error-message">
          <XCircleIcon size={14} /> <strong>Validation Error:</strong> {error}
        </div>
      )}

      {!error && (
        <div className="success-message">
          <CheckIcon size={14} /> <strong>Valid Configuration</strong>
        </div>
      )}

      <div className="editor-buttons">
        <button
          type="button"
          onClick={handleValidate}
          disabled={readonly}
          className="btn btn-secondary"
        >
          Validate JSON
        </button>
        <button
          type="button"
          onClick={handleFormatJson}
          disabled={readonly}
          className="btn btn-secondary"
        >
          Format
        </button>
      </div>

      <div className="info-box">
        <p>
          <LightbulbIcon size={14} /> <strong>Tip:</strong> Use this tab for advanced configuration or to troubleshoot
          filter configurations.
        </p>
      </div>
    </fieldset>
  );
};

export default AdvancedTab;
